import { useTranslation } from "react-i18next";
import {
  ColorPicker,
  LabelledSwitch,
  useDiscardModal,
} from "senf-atomic-design-system";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import Seperator from "senf-atomic-design-system/src/components/core/seperator/Seperator";
import Select from "senf-atomic-design-system/src/components/core/select/Select";
import { useFeatureAttributes } from "../functions/useFeatureAttributes";

const ColorSelector = ({ legendAttributes, handleColorChange }) => (
  <div className="flex flex-col gap-2">
    {legendAttributes
      ?.sort((a, b) => {
        if (a?.title < b?.title) {
          return -1; // a comes before b
        }
        if (a?.title > b?.title) {
          return 1; // b comes before a
        }
        return 0; // a and b are equivalent
      })
      ?.map((layer) => (
        <div
          className="flex items-center gap-2"
          key={layer.title}
        >
          <ColorPicker
            initialColor={layer.color}
            initialOpacity={layer?.opacity}
            handleSave={(color, opacity) => {
              handleColorChange(layer.title, color, opacity);
            }}
          />
          <span className="body-md">{layer.title}</span>
        </div>
      ))}
  </div>
);

const GisDataset = ({ formik, index, initialColor }) => {
  const colorPalette = [
    initialColor,
    "#fed062",
    "#bd98f6",
    "#91dff4",
    "#8dd9b8",
    "#929df6",
    "#e8907e",
    "#f6c095",
  ];

  const { t } = useTranslation();
  const { displayDiscardModal } = useDiscardModal();
  const { attributes, handleColorChange, generalColor } = useFeatureAttributes(
    formik,
    index,
    initialColor
  );

  const colorAttributes = [...attributes];
  colorAttributes.unshift({
    value: "none",
    label: t("map:builder.colorizeAllSame"),
  });

  const tooltipAttributes = [...attributes];
  tooltipAttributes.unshift({
    value: "none",
    label: t("global:none"),
  });

  const data = formik.values.gisData?.[index];
  const legendAttributes = data?.legendAttributes;

  return (
    <div>
      <div className="flex flex-col gap-2 rounded-xl border p-3">
        <div className="flex items-center justify-between gap-2">
          <span className="body-md-bold line-clamp-1  break-words">
            {formik.values.gisData?.[index]?.layerTitle}
          </span>
          <Button
            leadingIcon="trash"
            critical
            variant="shapeless"
            onClick={() => {
              displayDiscardModal({
                title: t("util:delete", { val: t("global:data") }),
                description: t("global:deleteDescription"),
                submitText: t("global:delete"),
                type: "onlyCloseDiscardModal",
                onSubmit: async () => {
                  formik.setFieldValue(
                    "gisData",
                    formik.values.gisData.filter((_, i) => i !== index)
                  );
                },
              });
            }}
          />
        </div>
        <Seperator className="-mx-3 py-2" />
        {/* <GeneralSettings
              formik={formik}
              index={index}
            /> */}
        <Select
          labelOption="inline"
          label={`${t("global:label")}:`}
          text={
            tooltipAttributes.find(
              ({ value }) => value === data?.selectedTooltip
            )?.label || t("global:none")
          }
          selectState="default"
          onValueChange={(value) => {
            const newData = { ...data.data };

            // Check if withTooltips flag is true
            newData.features = newData.features.map((feature) => {
              console.log(feature);
              return {
                ...feature,
                properties: {
                  ...feature.properties,
                  tooltipLabel: feature.properties[value] || value || null,
                },
              };
            });

            formik.setFieldValue(`gisData[${index}].selectedTooltip`, value);
            formik.setFieldValue(`gisData[${index}].data`, newData);
          }}
          data={tooltipAttributes.map(({ value, label }) => ({
            value,
            text: label,
          }))}
          useTriggerWidth={true}
        />
        <Select
          text={
            colorAttributes?.find(
              ({ value }) => value === data?.selectedAttribute
            )?.label || t("map:builder.colorizeAllSame")
          }
          labelOption="inline"
          label={`${t("global:color")}:`}
          selectState="default"
          onValueChange={(value) => {
            const newData = { ...data?.data }; // Making a shallow copy of the data

            const uniqueValues = [
              ...new Set(
                data?.data.features.map((feature) => feature.properties[value])
              ),
            ];
            const newLegendAttributes = uniqueValues.map((value, index) => ({
              title: value,
              color:
                legendAttributes?.find((layer) => layer.title === value)
                  ?.color || colorPalette[index % colorPalette.length],
            }));

            newData.features = newData.features.map((feature) => {
              const relatedLayer = newLegendAttributes.find(
                (layer) => layer.title === feature.properties[value]
              );

              return {
                ...feature,
                properties: {
                  ...feature.properties,
                  color: relatedLayer?.color,
                },
              };
            });

            formik.setFieldValue(
              `gisData[${index}].legendAttributes`,
              newLegendAttributes
            );
            formik.setFieldValue(`gisData[${index}].selectedAttribute`, value);
            formik.setFieldValue(`gisData[${index}].data`, newData);
          }}
          data={colorAttributes?.map(({ value, label }) => ({
            value,
            text: label,
          }))}
          useTriggerWidth={true}
        />
        <div className="flex flex-col gap-2">
          {data?.selectedAttribute === "none" && (
            <ColorSelector
              legendAttributes={[
                {
                  color:
                    data?.data?.features?.[0]?.properties?.color ||
                    generalColor,
                  opacity: data?.data?.features?.[0]?.properties?.opacity || 1,
                  title: t("global:color"),
                },
              ]}
              handleColorChange={handleColorChange}
            />
          )}

          {data?.selectedAttribute && data?.selectedAttribute !== "none" && (
            <div className="flex flex-col gap-2">
              <ColorSelector
                legendAttributes={legendAttributes}
                handleColorChange={handleColorChange}
              />
              <Seperator className="-ml-3" />
              <LabelledSwitch
                alignSwitch="left"
                name={`gisData[${index}].withLegend`}
                label={"In der Legende anzeigen"}
                labelStyle="body-md"
                toggled={data?.withLegend}
                handleSetToggle={() => {
                  formik.setFieldValue(
                    `gisData[${index}].withLegend`,
                    !data.withLegend
                  );
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GisDataset;
